import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ClipboardCheck, Loader2 } from "lucide-react";

const STATUS_OPTS = [
  { v: "conforme", label: "C", title: "Conforme", cls: "bg-green-600 text-white border-green-600" },
  { v: "nao_conforme", label: "NC", title: "Não conforme", cls: "bg-red-600 text-white border-red-600" },
  { v: "na", label: "N/A", title: "Não aplicável", cls: "bg-slate-500 text-white border-slate-500" },
];

export default function PmocDialog({ equipmentId, requestId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [equipment, setEquipment] = useState(null);
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [nextDate, setNextDate] = useState("");
  const [generalNotes, setGeneralNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !equipmentId) return;
    (async () => {
      setLoading(true);
      try {
        const { data: eq } = await api.get(`/equipment/${equipmentId}`);
        setEquipment(eq);
        const { data: tpl } = await api.get("/pmoc/template", { params: { equipment_type: eq.equipment_type } });
        setItems(tpl.items);
        const init = {};
        tpl.items.forEach((i) => { init[i.key] = { status: "conforme", note: "" }; });
        setAnswers(init);
        setServiceDate(new Date().toISOString().slice(0, 10));
        setNextDate(""); setGeneralNotes("");
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, equipmentId]);

  const setStatus = (key, status) => setAnswers((a) => ({ ...a, [key]: { ...a[key], status } }));
  const setNote = (key, note) => setAnswers((a) => ({ ...a, [key]: { ...a[key], note } }));

  const groups = items.reduce((acc, i) => {
    (acc[i.group || "Geral"] = acc[i.group || "Geral"] || []).push(i);
    return acc;
  }, {});

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        request_id: requestId || null,
        service_date: serviceDate,
        next_maintenance_date: nextDate || null,
        general_notes: generalNotes || null,
        items: items.map((i) => ({
          key: i.key, label: i.label, group: i.group,
          status: answers[i.key]?.status || "conforme",
          note: answers[i.key]?.note || null,
        })),
      };
      await api.post(`/equipment/${equipmentId}/pmoc`, payload);
      toast.success("PMOC registrado com sucesso!");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pmoc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-[#1e7be0]" /> Checklist PMOC</DialogTitle>
          <DialogDescription>
            {equipment ? `${equipment.equipment_type}${equipment.brand ? ` · ${equipment.brand}` : ""}${equipment.location ? ` — ${equipment.location}` : ""}` : "Carregando equipamento..."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Data do serviço</Label>
                <Input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} data-testid="pmoc-service-date" /></div>
              <div className="space-y-1.5"><Label>Próxima manutenção</Label>
                <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="pmoc-next-date" /></div>
            </div>

            {Object.entries(groups).map(([group, gitems]) => (
              <div key={group} className="space-y-2">
                <p className="text-xs font-semibold text-[#0a2a6c] uppercase tracking-wide">{group}</p>
                {gitems.map((i) => (
                  <div key={i.key} className="rounded-lg border border-slate-200 p-2.5" data-testid={`pmoc-item-${i.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-slate-700">{i.label}</span>
                      <div className="flex gap-1 flex-shrink-0">
                        {STATUS_OPTS.map((o) => {
                          const active = (answers[i.key]?.status || "conforme") === o.v;
                          return (
                            <button key={o.v} type="button" title={o.title}
                              onClick={() => setStatus(i.key, o.v)}
                              className={`h-7 w-9 rounded-md border text-xs font-semibold transition-colors ${active ? o.cls : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
                              data-testid={`pmoc-${i.key}-${o.v}`}>
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {answers[i.key]?.status === "nao_conforme" && (
                      <Input className="mt-2 h-8 text-sm" placeholder="Observação / ação tomada"
                        value={answers[i.key]?.note || ""} onChange={(e) => setNote(i.key, e.target.value)}
                        data-testid={`pmoc-note-${i.key}`} />
                    )}
                  </div>
                ))}
              </div>
            ))}

            <div className="space-y-1.5"><Label>Observações gerais</Label>
              <Textarea rows={3} value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)}
                placeholder="Resumo do atendimento, recomendações..." data-testid="pmoc-general-notes" /></div>
          </div>
        )}

        <DialogFooter>
          <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy || loading} onClick={submit} data-testid="submit-pmoc-button">
            {busy ? "Salvando..." : "Registrar PMOC"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
